// Derives dashboard signals from a case's audit trail.
//
// The Engine writes rich audit entries (scheduling.started, session.booked,
// action.drafted / action.released, document.generated) but does not yet
// expose list endpoints for scheduling tasks or draft actions to JWT
// sessions. The audit trail IS exposed (GET /cases/:id/audit), so Mission
// Control and the Case view mine it for: the case's scheduling task id,
// drafts awaiting human release, and the booked session. When the Engine
// grows first-class list endpoints, this file is the only thing to swap.

import type { AuditEntry } from '../types/dashboard';

export interface PendingDraft {
  /** Null when the audit entry predates action_id being recorded. */
  actionId: string | null;
  description: string;
  draftedAt: string;
}

export interface CaseInsights {
  schedulingTaskId: string | null;
  bookedSessionId: string | null;
  bookedSlot: string | null;
  pendingDrafts: PendingDraft[];
}

const detail = (e: AuditEntry, key: string): unknown => (e.details ?? {})[key];

export function deriveCaseInsights(entries: AuditEntry[]): CaseInsights {
  // Entries arrive newest-first from the API; keep that assumption local.
  const newestFirst = [...entries].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );

  let schedulingTaskId: string | null = null;
  let bookedSessionId: string | null = null;
  let bookedSlot: string | null = null;
  // A reschedule/cancel NEWER than a booking means that booking is dead —
  // without this, the Next-session panel kept advertising a cancelled
  // sitting with a working Join button, and manual-booked tasks were
  // invisible to the Scheduling panel (Scott, case 82be0638, 2026-11-02).
  let bookingDead = false;
  const TASK_EVENTS = new Set(['scheduling.started', 'scheduling.manual_booked', 'scheduling.rescheduled']);
  const BOOKING_KILLERS = new Set(['scheduling.rescheduled', 'scheduling.cancelled', 'calendar.event_removed']);

  for (const e of newestFirst) {
    if (!schedulingTaskId && TASK_EVENTS.has(e.event_type)) {
      const id = detail(e, 'task_id') ?? detail(e, 'scheduling_task_id');
      if (typeof id === 'string') schedulingTaskId = id;
    }
    if (!bookedSessionId && BOOKING_KILLERS.has(e.event_type)) bookingDead = true;
    if (!bookedSessionId && !bookingDead && e.event_type === 'session.booked') {
      const id = detail(e, 'session_id');
      if (typeof id === 'string') bookedSessionId = id;
      const slot = detail(e, 'slot');
      if (typeof slot === 'string') bookedSlot = slot;
    }
  }

  // Drafts pending release = drafted actions with no matching release.
  // Release events carry details.action_id; queued sends never re-enter
  // draft, so this set only ever shrinks.
  const releasedIds = new Set<string>();
  for (const e of newestFirst) {
    if (e.event_type === 'action.released') {
      const id = detail(e, 'action_id');
      if (typeof id === 'string') releasedIds.add(id);
    }
  }

  const pendingDrafts: PendingDraft[] = [];
  for (const e of newestFirst) {
    if (e.event_type !== 'action.drafted') continue;
    const rawId = detail(e, 'action_id');
    const actionId = typeof rawId === 'string' ? rawId : null;
    if (actionId && releasedIds.has(actionId)) continue;
    pendingDrafts.push({ actionId, description: e.description, draftedAt: e.timestamp });
  }

  return { schedulingTaskId, bookedSessionId, bookedSlot, pendingDrafts };
}

/** Run an async mapper over items with bounded concurrency. */
export async function mapLimit<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const i = next++;
      results[i] = await fn(items[i]);
    }
  });
  await Promise.all(workers);
  return results;
}
